import { sendUserApproveRejectQuote } from "@/models/socket/quotes.model";
import { Button } from "@/shadcn/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shadcn/components/ui/dialog";
import { FullSingleQuote } from "@/types";
import { genericFailureToast, genericSuccessToast } from "@/utils/utils.util";
import React, { useState } from "react";

type QuoteAcceptDialogProps = {
  quoteDbID: string;
  quoteDisplayID: string;
  updateQuoteStatus: (newStatus: FullSingleQuote["quote_status"]) => void;
};

const QuoteAcceptDialog: React.FC<QuoteAcceptDialogProps> = ({
  quoteDbID,
  quoteDisplayID,
  updateQuoteStatus, // function to update quote status in parent component
}) => {
  const [acceptReason, setAcceptReason] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<boolean>(false); // allows programmatic dialog close

  async function acceptQuote() {
    if (acceptReason.length > 255) {
      setError("Acceptance reason cannot exceed 255 characters.");
      return;
    }

    // using open state so we can close dialog after success
    setSubmitting(true);
    setError(null);

    const response = await sendUserApproveRejectQuote(
      quoteDbID,
      "accept",
      acceptReason.trim() // reason is optional, so we just trim and send
    );

    if (response.rStatus === "success") {
      genericSuccessToast("Quote accepted");
      setOpen(false); // close dialog on success for better UX
      updateQuoteStatus("accepted"); // update status in parent component
    } else {
      genericFailureToast("Failed to accept quote: " + response.rData);
      setError(response.rData);
    }

    setSubmitting(false);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        setOpen(open); // keep open state in sync
        if (!open) {
          setTimeout(() => {
            setAcceptReason("");
            setError(null);
            setSubmitting(false);
          }, 100); // reset state after dialog closes
        }
      }}
    >
      <DialogTrigger asChild>
        <Button className="">Approve</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Accept Quote: {quoteDisplayID}</DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p>
            Are you sure you want to accept the quote?
            <br />
            You may provide a reason/note with acceptance.
          </p>
          {error && <div className="text-sm text-red-500">{error}</div>}
          <textarea
            value={acceptReason}
            onChange={(e) => setAcceptReason(e.target.value)}
            placeholder="Enter acceptance note (optional)"
            className="w-full rounded border p-2"
            rows={4}
          />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button className="" onClick={acceptQuote} disabled={submitting}>
            {submitting ? "Submitting..." : "Approve Quote"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteAcceptDialog;
